import LeadCaptureService from "@/services/api/leadCapture";
import { handleResponse } from "@/services/axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface LeadCaptureState {
    leadOverviewList: any[];
    leadFieldMasterList: any[];
    leadSubmittedFormList: any[];
    embedCode: any;
    embed: any;
    loading: {
        leadOverview: boolean;
        leadFieldMaster: boolean;
        leadSubmittedForm: boolean;
        embedCode: boolean;
        embed: boolean;
    };
    error: string | null;
}

const initialState: LeadCaptureState = {
    leadOverviewList: [],
    leadFieldMasterList: [],
    leadSubmittedFormList: [],
    embedCode: null,
    embed: null,
    loading: {
        leadOverview: false,
        leadFieldMaster: false,
        leadSubmittedForm: false,
        embedCode: false,
        embed: false,
    },
    error: null,
};

export const getLeadForms = createAsyncThunk(
    "lead/getLeadForms",
    async (params: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getLeadForms(params);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch lead forms");
        }
    }
);

export const getLeadFormDetails = createAsyncThunk(
    "lead/getLeadFormDetails",
    async (params: { formCode: string }, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getLeadFormDetails(params);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch lead details");
        }
    }
);

export const getAllFieldMasters = createAsyncThunk(
    "lead/getAllFieldMasters",
    async (_, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getAllFieldMasters();
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch field master list");
        }
    }
);

export const addLeadForm = createAsyncThunk(
    "lead/addLeadForm",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.addLeadForm(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to add lead form");
        }
    }
);

export const updateLeadForm = createAsyncThunk(
    "lead/updateLeadForm",
    async (payload: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.updateLeadForm(payload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to update lead form");
        }
    }
);

export const getLeadSubmittedForm = createAsyncThunk(
    "lead/getLeadSubmittedForm",
    async (params: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getLeadSubmittedForm(params);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch lead submitted form list");
        }
    }
);

export const getLeadSubmittedFormById = createAsyncThunk(
    "lead/getLeadSubmittedFormById",
    async (params: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getLeadSubmittedFormById(params);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch lead submitted form details");
        }
    }
);

export const addSubmitForm = createAsyncThunk(
    "lead/addSubmitForm",
    async (data: { payload: any, parentOrigin: string }, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.addSubmitForm(data.payload, data.parentOrigin);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to add details form");
        }
    }
);

export const getEmbedCode = createAsyncThunk(
    "lead/getEmbedCode",
    async (formCode: string, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getEmbedCode(formCode);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch embed code");
        }
    }
);

export const getEmbed = createAsyncThunk(
    "lead/getEmbed",
    async (data: { formCode: string, parentOrigin: string }, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.getEmbed(data.formCode, data.parentOrigin);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to fetch embed");
        }
    }
);

export const updateRequestStatus = createAsyncThunk(
    "lead/updateRequestStatus",
    async (updatedPayload: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.updateRequestStatus(updatedPayload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to update request status");
        }
    }
);

export const updatePublishFormStatus = createAsyncThunk(
    "lead/updatePublishFormStatus",
    async (updatedPayload: any, { rejectWithValue }) => {
        try {
            const res = await LeadCaptureService.updatePublishFormStatus(updatedPayload);
            return handleResponse(res, rejectWithValue);
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.Message || "Failed to update publish status");
        }
    }
);

const leadCaptureSlice = createSlice({
    name: "lead",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getLeadForms.pending, (state) => {
                state.error = null;
                state.loading.leadOverview = true;
            })
            .addCase(getLeadForms.fulfilled, (state, action) => {
                state.error = null;
                state.leadOverviewList = action.payload || [];
                state.loading.leadOverview = false;
            })
            .addCase(getLeadForms.rejected, (state, action) => {
                state.error = action.payload as string;
                state.leadOverviewList = [];
                state.loading.leadOverview = false;
            })

            .addCase(getAllFieldMasters.pending, (state) => {
                state.error = null;
                state.loading.leadFieldMaster = true;
            })
            .addCase(getAllFieldMasters.fulfilled, (state, action) => {
                state.error = null;
                state.leadFieldMasterList = action.payload || [];
                state.loading.leadFieldMaster = false;
            })
            .addCase(getAllFieldMasters.rejected, (state, action) => {
                state.error = action.payload as string;
                state.leadFieldMasterList = [];
                state.loading.leadFieldMaster = false;
            })

            .addCase(getLeadSubmittedForm.pending, (state) => {
                state.error = null;
                state.loading.leadSubmittedForm = true;
            })
            .addCase(getLeadSubmittedForm.fulfilled, (state, action) => {
                state.error = null;
                state.leadSubmittedFormList = action.payload || [];
                state.loading.leadSubmittedForm = false;
            })
            .addCase(getLeadSubmittedForm.rejected, (state, action) => {
                state.error = action.payload as string;
                state.leadSubmittedFormList = [];
                state.loading.leadSubmittedForm = false;
            })

            .addCase(getEmbedCode.pending, (state) => {
                state.error = null;
                state.loading.embedCode = true;
            })
            .addCase(getEmbedCode.fulfilled, (state, action) => {
                state.error = null;
                state.embedCode = action.payload;
                state.loading.embedCode = false;
            })
            .addCase(getEmbedCode.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.embedCode = false;
            })

            .addCase(getEmbed.pending, (state) => {
                state.error = null;
                state.loading.embed = true;
            })
            .addCase(getEmbed.fulfilled, (state, action) => {
                state.error = null;
                state.embed = action.payload;
                state.loading.embed = false;
            })
            .addCase(getEmbed.rejected, (state, action) => {
                state.error = action.payload as string;
                state.loading.embed = false;
            });
    },
});

export default leadCaptureSlice.reducer;